import React, { useEffect, useMemo, useState } from 'react'
import axios from 'axios'
import {
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Paper,
  TextField,
  Typography
} from '@material-ui/core'
import Autocomplete from '@material-ui/lab/Autocomplete'
import {
  CheckCircleOutline as CheckIcon,
  ChevronRight as ChevronRightIcon,
  ExpandMore as ExpandMoreIcon,
  Search as SearchIcon
} from '@material-ui/icons'

const STATUS_FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'needs', label: 'Needs match' },
  { id: 'suggested', label: 'Suggested' },
  { id: 'split', label: 'Split' },
  { id: 'assigned', label: 'Assigned' },
  { id: 'output', label: 'Output-only' },
  { id: 'out', label: 'Out of scope' }
]

const statusStyles = {
  assigned: { background: 'var(--df-ok-bg)', color: 'var(--df-ok-text)', borderColor: '#6b9b72' },
  split: { background: 'var(--df-accent-soft)', color: 'var(--df-accent-2)', borderColor: 'var(--df-accent)' },
  suggested: { background: 'var(--df-warn-bg)', color: 'var(--df-warn-text)', borderColor: '#d7a442' },
  needs: { background: 'var(--df-danger-bg)', color: 'var(--df-danger-text)', borderColor: '#d48476' },
  output: { background: 'var(--df-muted-bg)', color: 'var(--df-muted-text)', borderColor: 'var(--df-line-2)' },
  out: { background: '#f2f2ec', color: '#6f746b', borderColor: '#d4d0c2' }
}

const serviceSearchText = (service) => [
  service?.name,
  service?.serviceName,
  service?.serviceId,
  service?.category,
  service?.serviceCategory,
  ...(service?.aliases || [])
].filter(Boolean).join(' ').toLowerCase()

const filterServiceOptions = (options, state) => {
  const query = String(state.inputValue || '').trim().toLowerCase()
  if (!query) return options
  return options.filter(service => serviceSearchText(service).includes(query))
}

const formatQty = (value) => {
  const number = Number(value || 0)
  if (!Number.isFinite(number)) return ''
  return Number.isInteger(number) ? String(number) : String(Number(number.toFixed(4)))
}

const getTopSuggestion = (row) => (row.suggestions || [])[0] || null

const getRowStatus = (row) => {
  if (row.disposition === 'out_of_scope') return 'out'
  if (row.disposition === 'output_only') return 'output'
  if ((row.assignments || []).length > 1) return 'split'
  if ((row.assignments || []).length === 1) return 'assigned'
  if (getTopSuggestion(row)?.confidence >= 0.7) return 'suggested'
  return 'needs'
}

const getRowStatusLabel = (row) => {
  const status = getRowStatus(row)
  if (status === 'assigned') return row.assignments[0].serviceName
  if (status === 'split') return `${row.assignments.length}-way split`
  if (status === 'suggested') return `Suggest: ${getTopSuggestion(row).serviceName}`
  if (status === 'output') return 'Output-only'
  if (status === 'out') return 'Out of scope'
  return 'Needs match'
}

const ServicePicker = ({ services, value, onChange, placeholder = 'Pick service' }) => {
  const options = useMemo(() => {
    return (services || []).slice().sort((a, b) => {
      const categoryDiff = String(a.category || '').localeCompare(String(b.category || ''))
      if (categoryDiff !== 0) return categoryDiff
      return String(a.name || '').localeCompare(String(b.name || ''))
    })
  }, [services])

  return (
    <Autocomplete
      options={options}
      groupBy={option => option.category || 'Other'}
      getOptionLabel={option => option?.name || ''}
      getOptionSelected={(option, selected) => option.serviceId === selected.serviceId}
      filterOptions={filterServiceOptions}
      value={value || null}
      onChange={(event, next) => onChange(next)}
      size="small"
      renderOption={(option) => (
        <div style={{ minWidth: 0 }}>
          <Typography variant="body2" style={{ fontWeight: 700 }}>{option.name}</Typography>
          <Typography variant="caption" style={{ color: 'var(--df-ink-3)', fontFamily: 'var(--df-font-mono)' }}>
            {option.serviceId}
          </Typography>
        </div>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          variant="outlined"
          placeholder={placeholder}
        />
      )}
    />
  )
}

const CoverageStrip = ({ counts }) => {
  const total = counts.total || 0
  const segments = [
    { key: 'assigned', label: 'Assigned', value: counts.assigned || 0, color: 'var(--df-primary)' },
    { key: 'split', label: 'Split', value: counts.split || 0, color: 'var(--df-accent)' },
    { key: 'suggested', label: 'Suggested', value: counts.suggested || 0, color: '#d6a33d' },
    { key: 'needsMatch', label: 'Needs match', value: counts.needsMatch || 0, color: '#b54a31' },
    { key: 'outputOnly', label: 'Output-only', value: counts.outputOnly || 0, color: 'var(--df-ink-4)' },
    { key: 'outOfScope', label: 'Out of scope', value: counts.outOfScope || 0, color: '#c5c1b4' }
  ]

  return (
    <Paper style={{ padding: 18, borderRadius: 12, border: '1px solid var(--df-line)', boxShadow: 'var(--df-shadow-sm)', background: 'var(--df-surface)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 16 }}>
        <Typography variant="caption" style={{ fontFamily: 'var(--df-font-mono)', letterSpacing: 1.5, color: 'var(--df-ink-3)', fontWeight: 800 }}>
          BID TO SERVICE COVERAGE
        </Typography>
        <Typography style={{ fontSize: 28, fontWeight: 900, color: 'var(--df-ink)' }}>{counts.coveragePct || 0}%</Typography>
      </div>
      <div style={{ height: 13, display: 'flex', background: 'var(--df-bg-2)', borderRadius: 999, overflow: 'hidden', marginTop: 10, border: '1px solid var(--df-line)' }}>
        {segments.map(segment => {
          const width = total ? `${(segment.value / total) * 100}%` : '0%'
          return <div key={segment.key} style={{ width, background: segment.color }} />
        })}
      </div>
      <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', marginTop: 14 }}>
        {segments.map(segment => (
          <div key={segment.key} style={{ display: 'flex', gap: 7, alignItems: 'center', color: 'var(--df-ink-3)', fontSize: 13 }}>
            <span style={{ width: 9, height: 9, borderRadius: 2, background: segment.color }} />
            <strong style={{ color: 'var(--df-ink-2)' }}>{segment.value}</strong>
            {segment.label}
          </div>
        ))}
      </div>
    </Paper>
  )
}

const SplitEditor = ({ row, services, onApply, onCancel }) => {
  const initial = row.assignments && row.assignments.length
    ? row.assignments
    : (getTopSuggestion(row) ? [{
        serviceId: getTopSuggestion(row).serviceId,
        serviceName: getTopSuggestion(row).serviceName,
        serviceCategory: getTopSuggestion(row).serviceCategory,
        pct: 100,
        allocationMode: 'pct'
      }] : [])
  const [slices, setSlices] = useState(initial)
  const totalPct = slices.reduce((sum, slice) => sum + Number(slice.pct || 0), 0)
  const qty = Number(row.quantity || 0)

  const updateSlice = (index, patch) => {
    setSlices(prev => prev.map((slice, i) => i === index ? { ...slice, ...patch } : slice))
  }

  const addSlice = () => {
    const remaining = Math.max(0, 100 - totalPct)
    setSlices(prev => [...prev, { serviceId: '', serviceName: '', serviceCategory: '', pct: remaining || 0, allocationMode: 'pct' }])
  }

  const balance = () => {
    if (slices.length === 0) return
    const pct = Number((100 / slices.length).toFixed(2))
    setSlices(prev => prev.map((slice, index) => ({
      ...slice,
      pct: index === prev.length - 1 ? Number((100 - pct * (prev.length - 1)).toFixed(2)) : pct
    })))
  }

  return (
    <div style={{ border: '1px solid var(--df-line)', borderRadius: 10, padding: 12, background: 'var(--df-surface)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <Typography variant="subtitle2" style={{ fontWeight: 800 }}>Split this row between services</Typography>
        <Button size="small" onClick={balance}>Balance evenly</Button>
      </div>
      <div style={{ display: 'grid', gap: 10 }}>
        {slices.map((slice, index) => {
          const service = services.find(s => s.serviceId === slice.serviceId) || null
          return (
            <div key={index} style={{ display: 'grid', gridTemplateColumns: 'minmax(220px, 1fr) 110px 110px 34px', gap: 8, alignItems: 'center' }}>
              <ServicePicker
                services={services}
                value={service}
                onChange={(next) => updateSlice(index, {
                  serviceId: next?.serviceId || '',
                  serviceName: next?.name || '',
                  serviceCategory: next?.category || ''
                })}
              />
              <TextField
                size="small"
                variant="outlined"
                type="number"
                label="%"
                value={slice.pct}
                onChange={(event) => updateSlice(index, { pct: Number(event.target.value || 0), allocationMode: 'pct' })}
              />
              <Typography variant="body2" style={{ color: 'var(--df-ink-3)', fontFamily: 'var(--df-font-mono)' }}>
                ~ {formatQty(qty * Number(slice.pct || 0) / 100)} {row.unit}
              </Typography>
              <IconButton size="small" onClick={() => setSlices(prev => prev.filter((s, i) => i !== index))}>x</IconButton>
            </div>
          )
        })}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12 }}>
        <Button size="small" onClick={addSlice}>+ Add slice</Button>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Typography variant="caption" style={{ color: Math.abs(totalPct - 100) <= 0.1 ? 'var(--df-ok-text)' : 'var(--df-danger-text)', fontWeight: 800 }}>
            Total {Number(totalPct.toFixed(2))}%
          </Typography>
          <Button size="small" onClick={onCancel}>Cancel</Button>
          <Button
            size="small"
            variant="contained"
            color="primary"
            disabled={slices.length === 0 || slices.some(s => !s.serviceId) || Math.abs(totalPct - 100) > 0.1}
            onClick={() => onApply(slices.map(slice => ({
              serviceId: slice.serviceId,
              serviceName: slice.serviceName,
              serviceCategory: slice.serviceCategory,
              pct: Number(slice.pct || 0),
              allocationMode: slice.allocationMode || 'pct',
              source: 'split'
            })))}
          >
            Apply split
          </Button>
        </div>
      </div>
    </div>
  )
}

const BidRowExpanded = ({ row, services, onAssign, onClear, onDisposition }) => {
  const [picked, setPicked] = useState(null)
  const [splitting, setSplitting] = useState(false)
  const suggestions = row.suggestions || []

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(360px, 0.9fr)', gap: 18, padding: '0 18px 18px 52px' }}>
      <div style={{ color: 'var(--df-ink-2)' }}>
        <Typography variant="caption" style={{ fontFamily: 'var(--df-font-mono)', color: 'var(--df-ink-3)', fontWeight: 800 }}>FROM BID SCHEDULE</Typography>
        <Typography variant="body2" style={{ marginTop: 8, lineHeight: 1.5 }}>{row.description}</Typography>
        <div style={{ display: 'flex', gap: 18, marginTop: 12, color: 'var(--df-ink-3)' }}>
          <span><strong>{formatQty(row.quantity)}</strong> {row.unit}</span>
          <span>{row.itemType}</span>
          {row.category && <span>{row.category}</span>}
          {row.sourcePage && <span>page {row.sourcePage}</span>}
        </div>
      </div>
      <div style={{ display: 'grid', gap: 12 }}>
        {splitting ? (
          <SplitEditor
            row={row}
            services={services}
            onApply={slices => {
              onAssign(row, slices)
              setSplitting(false)
            }}
            onCancel={() => setSplitting(false)}
          />
        ) : (
          <>
            <div style={{ display: 'grid', gap: 8 }}>
              <Typography variant="caption" style={{ fontFamily: 'var(--df-font-mono)', color: 'var(--df-ink-3)', fontWeight: 800 }}>ASSIGN SERVICE</Typography>
              {suggestions.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {suggestions.slice(0, 3).map(suggestion => (
                    <Button
                      key={suggestion.serviceId}
                      size="small"
                      variant="outlined"
                      onClick={() => onAssign(row, [{
                        serviceId: suggestion.serviceId,
                        serviceName: suggestion.serviceName,
                        serviceCategory: suggestion.serviceCategory,
                        pct: 100,
                        allocationMode: 'pct',
                        source: 'suggested_accepted',
                        confidenceAtAssign: suggestion.confidence
                      }])}
                      style={{ borderColor: 'var(--df-line-2)', textTransform: 'none' }}
                    >
                      {suggestion.serviceName} - {Math.round(suggestion.confidence * 100)}%
                    </Button>
                  ))}
                </div>
              )}
              <div style={{ display: 'grid', gridTemplateColumns: 'minmax(220px, 1fr) auto', gap: 8 }}>
                <ServicePicker services={services} value={picked} onChange={setPicked} placeholder="Pick service manually" />
                <Button
                  variant="contained"
                  color="primary"
                  disabled={!picked}
                  onClick={() => onAssign(row, [{
                    serviceId: picked.serviceId,
                    serviceName: picked.name,
                    serviceCategory: picked.category,
                    pct: 100,
                    allocationMode: 'pct',
                    source: 'manual_pick'
                  }])}
                >
                  Assign
                </Button>
              </div>
            </div>
            <Divider />
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
              <Button size="small" onClick={() => setSplitting(true)}>Split between services</Button>
              <div style={{ display: 'flex', gap: 8 }}>
                {(row.assignments || []).length > 0 && <Button size="small" onClick={() => onClear(row)}>Clear assignment</Button>}
                {row.disposition !== 'output_only' && (
                  <Button size="small" onClick={() => onDisposition(row, 'output_only')}>Mark output-only</Button>
                )}
                {row.disposition !== 'out_of_scope' && (
                  <Button size="small" onClick={() => onDisposition(row, 'out_of_scope')}>Mark out of scope</Button>
                )}
                {row.disposition !== 'none' && <Button size="small" onClick={() => onDisposition(row, 'none')}>Undo disposition</Button>}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

const BidRow = ({ row, expanded, onToggle, services, onAssign, onClear, onDisposition, onServiceFilter }) => {
  const status = getRowStatus(row)
  const style = statusStyles[status]
  const suggestion = getTopSuggestion(row)

  const acceptSuggestion = (event) => {
    event.stopPropagation()
    if (!suggestion) return
    onAssign(row, [{
      serviceId: suggestion.serviceId,
      serviceName: suggestion.serviceName,
      serviceCategory: suggestion.serviceCategory,
      pct: 100,
      allocationMode: 'pct',
      source: 'suggested_accepted',
      confidenceAtAssign: suggestion.confidence
    }])
  }

  const clickAssignment = (event) => {
    event.stopPropagation()
    const assignment = (row.assignments || [])[0]
    if (assignment && onServiceFilter) onServiceFilter(assignment.serviceId)
  }

  return (
    <Paper style={{ borderRadius: 10, border: `1px solid ${expanded ? 'var(--df-primary)' : 'var(--df-line)'}`, boxShadow: 'none', background: row.disposition === 'out_of_scope' ? '#f3f1eb' : 'var(--df-surface)', overflow: 'hidden' }}>
      <div
        onClick={onToggle}
        style={{
          display: 'grid',
          gridTemplateColumns: '34px 58px minmax(0, 1fr) 90px 280px',
          gap: 12,
          alignItems: 'center',
          padding: '13px 16px',
          cursor: 'pointer'
        }}
      >
        <IconButton size="small">
          {expanded ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
        </IconButton>
        <Typography style={{ fontFamily: 'var(--df-font-mono)', fontWeight: 800, color: 'var(--df-ink-3)' }}>{row.itemNo || '-'}</Typography>
        <Typography style={{ fontWeight: 700, color: 'var(--df-ink)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {row.description}
        </Typography>
        <Typography style={{ color: 'var(--df-ink-3)', textAlign: 'right' }}>
          <strong>{formatQty(row.quantity)}</strong> {row.unit}
        </Typography>
        <Chip
          size="small"
          clickable={status === 'suggested' || status === 'assigned' || status === 'split'}
          onClick={status === 'suggested' ? acceptSuggestion : clickAssignment}
          icon={status === 'assigned' ? <CheckIcon /> : undefined}
          label={status === 'suggested' && suggestion ? `${getRowStatusLabel(row)} - ${Math.round(suggestion.confidence * 100)}%` : getRowStatusLabel(row)}
          style={{
            justifySelf: 'end',
            maxWidth: 270,
            border: `1px solid ${style.borderColor}`,
            ...style,
            fontWeight: 800
          }}
        />
      </div>
      {expanded && (
        <BidRowExpanded
          row={row}
          services={services}
          onAssign={onAssign}
          onClear={onClear}
          onDisposition={onDisposition}
        />
      )}
    </Paper>
  )
}

const ServicesRail = ({ projectServices, rows, activeServiceId, onServiceFilter, onAddService }) => {
  const suggested = useMemo(() => {
    const onProject = new Set((projectServices || []).map(service => service.serviceId))
    const byId = new Map()
    for (const row of rows || []) {
      if ((row.assignments || []).length > 0 || row.disposition !== 'none') continue
      const suggestion = getTopSuggestion(row)
      if (!suggestion || suggestion.confidence < 0.7 || onProject.has(suggestion.serviceId)) continue
      const current = byId.get(suggestion.serviceId) || { ...suggestion, rowCount: 0 }
      current.rowCount += 1
      byId.set(suggestion.serviceId, current)
    }
    return Array.from(byId.values()).sort((a, b) => b.rowCount - a.rowCount)
  }, [projectServices, rows])

  const grouped = useMemo(() => {
    const groups = {}
    for (const service of projectServices || []) {
      const key = service.serviceCategory || 'Other'
      if (!groups[key]) groups[key] = []
      groups[key].push(service)
    }
    return groups
  }, [projectServices])

  return (
    <aside style={{ position: 'sticky', top: 0, alignSelf: 'start', height: 'calc(100vh - 170px)', overflow: 'auto', borderLeft: '1px solid var(--df-line)', background: 'var(--df-surface)', padding: 18 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <div>
          <Typography variant="h6" style={{ fontWeight: 900 }}>Project services</Typography>
          <Typography variant="caption" style={{ color: 'var(--df-ink-3)' }}>{(projectServices || []).length} on project</Typography>
        </div>
        <Button size="small" variant="outlined" onClick={onAddService}>+ Add</Button>
      </div>
      {Object.keys(grouped).sort().map(category => (
        <div key={category} style={{ marginBottom: 18 }}>
          <Typography variant="caption" style={{ fontFamily: 'var(--df-font-mono)', letterSpacing: 1.6, color: 'var(--df-ink-3)', fontWeight: 900 }}>
            {category}
          </Typography>
          <div style={{ display: 'grid', gap: 7, marginTop: 8 }}>
            {grouped[category].map(service => {
              const active = activeServiceId === service.serviceId
              return (
                <button
                  key={service.serviceId}
                  type="button"
                  onClick={() => onServiceFilter(active ? null : service.serviceId)}
                  style={{
                    textAlign: 'left',
                    border: `1px solid ${active ? 'var(--df-primary)' : 'transparent'}`,
                    background: active ? 'var(--df-primary-soft)' : 'transparent',
                    borderRadius: 8,
                    padding: '8px 10px',
                    cursor: 'pointer',
                    color: 'var(--df-ink)'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                    <strong>{service.serviceName}</strong>
                    <span style={{ borderRadius: 999, background: 'var(--df-bg-2)', padding: '1px 8px', fontWeight: 900 }}>{service.linkedRowCount}</span>
                  </div>
                </button>
              )
            })}
          </div>
        </div>
      ))}
      {suggested.length > 0 && (
        <>
          <Divider style={{ margin: '12px 0' }} />
          <Typography variant="caption" style={{ fontFamily: 'var(--df-font-mono)', letterSpacing: 1.6, color: 'var(--df-ink-3)', fontWeight: 900 }}>
            SUGGESTED
          </Typography>
          <div style={{ display: 'grid', gap: 8, marginTop: 8 }}>
            {suggested.map(service => (
              <div key={service.serviceId} style={{ border: '1px dashed var(--df-line-2)', borderRadius: 8, padding: 10 }}>
                <Typography variant="body2" style={{ fontWeight: 800 }}>{service.serviceName}</Typography>
                <Typography variant="caption" style={{ color: 'var(--df-ink-3)' }}>{service.rowCount} suggested row{service.rowCount === 1 ? '' : 's'}</Typography>
              </div>
            ))}
          </div>
        </>
      )}
    </aside>
  )
}

const MatchWorkspace = ({ packageId, url, onCountsChange, onUpdate }) => {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [expandedRows, setExpandedRows] = useState({})
  const [activeServiceId, setActiveServiceId] = useState(null)
  const [addServiceOpen, setAddServiceOpen] = useState(false)
  const [serviceToAdd, setServiceToAdd] = useState(null)
  const [generating, setGenerating] = useState(false)

  const fetchMatch = () => {
    if (!packageId) return
    setLoading(true)
    setError('')
    axios.get(`${url}/api/bid-estimation/bid-packages/${packageId}/match`)
      .then(res => {
        setData(res.data)
        if (onCountsChange) onCountsChange(res.data?.counts || {})
        setLoading(false)
      })
      .catch(err => {
        setError(err.response?.data?.message || 'Failed to load service matching workspace.')
        setLoading(false)
      })
  }

  useEffect(() => {
    fetchMatch()
  }, [packageId, url])

  const applyPayload = (payload) => {
    setData(payload)
    if (onCountsChange) onCountsChange(payload?.counts || {})
  }

  const assignRow = async (row, slices) => {
    setSaving(true)
    try {
      const res = await axios.put(`${url}/api/bid-estimation/bid-items/${row._id}/service-assignments`, { slices })
      applyPayload(res.data)
      setExpandedRows(prev => ({ ...prev, [row._id]: false }))
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || 'Failed to assign service.')
    } finally {
      setSaving(false)
    }
  }

  const clearRow = async (row) => {
    setSaving(true)
    try {
      const res = await axios.delete(`${url}/api/bid-estimation/bid-items/${row._id}/service-assignments`)
      applyPayload(res.data)
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to clear assignment.')
    } finally {
      setSaving(false)
    }
  }

  const setDisposition = async (row, disposition) => {
    setSaving(true)
    try {
      const res = await axios.put(`${url}/api/bid-estimation/bid-items/${row._id}/disposition`, { disposition })
      applyPayload(res.data)
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update row disposition.')
    } finally {
      setSaving(false)
    }
  }

  const addProjectService = async () => {
    if (!serviceToAdd) return
    // A service can only become "on project" when a row or downstream allocation owns it.
    // Until that model exists, this dialog simply filters rows by the picked service.
    setActiveServiceId(serviceToAdd.serviceId)
    setAddServiceOpen(false)
    setServiceToAdd(null)
  }

  const generateProjectServices = async () => {
    if (!packageId) return
    setGenerating(true)
    setError('')
    try {
      const res = await axios.post(`${url}/api/bid-estimation/project-services/from-bid-response/${packageId}`)
      const created = res.data?.created || 0
      const materialsCreated = res.data?.materialsCreated || 0
      const details = materialsCreated > 0
        ? ` Generated ${created} service${created === 1 ? '' : 's'} and ${materialsCreated} material${materialsCreated === 1 ? '' : 's'}.`
        : ` Generated ${created} service${created === 1 ? '' : 's'}.`
      setError(`Project services are ready.${details}`)
      if (onUpdate) onUpdate()
      fetchMatch()
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.message || 'Failed to generate project services.')
    } finally {
      setGenerating(false)
    }
  }

  const rows = data?.rows || []
  const services = data?.services || []
  const projectServices = data?.projectServices || []

  const countsByStatus = useMemo(() => {
    const counts = {}
    for (const row of rows) {
      const status = getRowStatus(row)
      counts[status] = (counts[status] || 0) + 1
    }
    counts.all = rows.length
    return counts
  }, [rows])

  const filteredRows = useMemo(() => {
    const query = search.trim().toLowerCase()
    return rows.filter(row => {
      if (query) {
        const haystack = [row.itemNo, row.description, row.groupName, row.unit, row.category].filter(Boolean).join(' ').toLowerCase()
        if (!haystack.includes(query)) return false
      }
      const status = getRowStatus(row)
      if (statusFilter !== 'all') {
        if (statusFilter === 'needs' && status !== 'needs') return false
        if (statusFilter === 'suggested' && status !== 'suggested') return false
        if (statusFilter === 'split' && status !== 'split') return false
        if (statusFilter === 'assigned' && status !== 'assigned') return false
        if (statusFilter === 'output' && status !== 'output') return false
        if (statusFilter === 'out' && status !== 'out') return false
      }
      if (activeServiceId) {
        const hasService = (row.assignments || []).some(assignment => assignment.serviceId === activeServiceId) ||
          getTopSuggestion(row)?.serviceId === activeServiceId
        if (!hasService) return false
      }
      return true
    })
  }, [rows, search, statusFilter, activeServiceId])

  const groups = useMemo(() => {
    const byGroup = new Map()
    for (const row of filteredRows) {
      const key = row.bidGroup?._id || row.groupName || 'ungrouped'
      if (!byGroup.has(key)) {
        byGroup.set(key, {
          key,
          name: row.groupName || 'Ungrouped',
          sequence: row.groupSequence || 0,
          rows: []
        })
      }
      byGroup.get(key).rows.push(row)
    }
    return Array.from(byGroup.values()).sort((a, b) => {
      const seq = (a.sequence || 0) - (b.sequence || 0)
      if (seq !== 0) return seq
      return String(a.name).localeCompare(String(b.name))
    })
  }, [filteredRows])

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
        <CircularProgress />
      </div>
    )
  }

  if (error && !data) {
    return <Typography color="error" style={{ padding: 24 }}>{error}</Typography>
  }

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 360px', minHeight: '100%', background: 'var(--df-bg)' }}>
      <main style={{ padding: '24px 28px 42px', minWidth: 0 }}>
        <div style={{ maxWidth: 1320, margin: '0 auto', display: 'grid', gap: 16 }}>
          <CoverageStrip counts={data?.counts || {}} />

          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              variant="outlined"
              size="small"
              placeholder="Search bid rows, items, codes..."
              InputProps={{ startAdornment: <SearchIcon fontSize="small" style={{ marginRight: 8, color: 'var(--df-ink-3)' }} /> }}
              style={{ minWidth: 320, background: 'var(--df-surface)' }}
            />
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {STATUS_FILTERS.map(filter => (
                <Button
                  key={filter.id}
                  size="small"
                  variant={statusFilter === filter.id ? 'contained' : 'outlined'}
                  color={statusFilter === filter.id ? 'primary' : 'default'}
                  onClick={() => setStatusFilter(filter.id)}
                  style={{ textTransform: 'none', fontWeight: 800 }}
                >
                  {filter.label} {countsByStatus[filter.id] || 0}
                </Button>
              ))}
            </div>
            {activeServiceId && (
              <Chip
                label={`Service filter: ${services.find(s => s.serviceId === activeServiceId)?.name || activeServiceId}`}
                onDelete={() => setActiveServiceId(null)}
                style={{ background: 'var(--df-primary-soft)', color: 'var(--df-primary)' }}
              />
            )}
            <div style={{ flex: 1 }} />
            <Button
              variant="contained"
              size="small"
              onClick={generateProjectServices}
              disabled={generating || !((data?.counts?.assigned || 0) + (data?.counts?.split || 0))}
              style={{ backgroundColor: 'var(--df-primary)', color: '#fff', textTransform: 'none', fontWeight: 800 }}
            >
              {generating ? 'Generating...' : 'Generate services'}
            </Button>
            {saving && <CircularProgress size={18} />}
          </div>

          {error && (
            <Paper style={{ padding: 12, border: '1px solid var(--df-line)', background: error.startsWith('Project services are ready') ? 'var(--df-ok-bg)' : 'var(--df-danger-bg)' }}>
              <Typography variant="body2" style={{ color: error.startsWith('Project services are ready') ? 'var(--df-ok-text)' : 'var(--df-danger-text)' }}>{error}</Typography>
            </Paper>
          )}

          <div style={{ display: 'grid', gap: 20 }}>
            {groups.map(group => {
              const groupCovered = group.rows.filter(row => !['needs', 'suggested'].includes(getRowStatus(row))).length
              const pct = group.rows.length ? Math.round((groupCovered / group.rows.length) * 100) : 0
              return (
                <section key={group.key} style={{ display: 'grid', gap: 8 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 180px 54px', gap: 12, alignItems: 'center', padding: '8px 4px' }}>
                    <Typography style={{ fontWeight: 900, letterSpacing: 0.8, color: 'var(--df-ink)' }}>
                      {group.name}
                      <Chip size="small" label={`${group.rows.length} rows`} style={{ marginLeft: 10, background: 'var(--df-bg-2)', color: 'var(--df-ink-3)', fontWeight: 800 }} />
                    </Typography>
                    <div style={{ height: 7, background: 'var(--df-bg-2)', borderRadius: 99, overflow: 'hidden' }}>
                      <div style={{ width: `${pct}%`, height: '100%', background: 'var(--df-primary)' }} />
                    </div>
                    <Typography variant="caption" style={{ textAlign: 'right', fontWeight: 800 }}>{pct}%</Typography>
                  </div>
                  <div style={{ display: 'grid', gap: 8 }}>
                    {group.rows.map(row => (
                      <BidRow
                        key={row._id}
                        row={row}
                        expanded={!!expandedRows[row._id]}
                        onToggle={() => setExpandedRows(prev => ({ ...prev, [row._id]: !prev[row._id] }))}
                        services={services}
                        onAssign={assignRow}
                        onClear={clearRow}
                        onDisposition={setDisposition}
                        onServiceFilter={setActiveServiceId}
                      />
                    ))}
                  </div>
                </section>
              )
            })}
          </div>
        </div>
      </main>

      <ServicesRail
        projectServices={projectServices}
        rows={rows}
        activeServiceId={activeServiceId}
        onServiceFilter={setActiveServiceId}
        onAddService={() => setAddServiceOpen(true)}
      />

      {addServiceOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(28,36,25,0.25)', zIndex: 1600, display: 'grid', placeItems: 'center' }} onClick={() => setAddServiceOpen(false)}>
          <Paper style={{ width: 520, padding: 20, borderRadius: 12 }} onClick={event => event.stopPropagation()}>
            <Typography variant="h6" style={{ fontWeight: 900, marginBottom: 6 }}>Find a service</Typography>
            <Typography variant="body2" style={{ color: 'var(--df-ink-3)', marginBottom: 14 }}>
              For this MVP, adding a service filters the bid rows so you can assign one manually. Rowless extra services will be allocated in the later refine step.
            </Typography>
            <ServicePicker services={services} value={serviceToAdd} onChange={setServiceToAdd} placeholder="Search services" />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
              <Button onClick={() => setAddServiceOpen(false)}>Cancel</Button>
              <Button variant="contained" color="primary" disabled={!serviceToAdd} onClick={addProjectService}>Focus service</Button>
            </div>
          </Paper>
        </div>
      )}
    </div>
  )
}

export default MatchWorkspace
